/**
 * Client detail — a full page, not a popup (build brief §12).
 *
 * Its own route (`/clients/:id`) with a breadcrumb back to the list, so an
 * account is linkable and the back button works. The old read-only sheet
 * (`modals/ClientView.jsx`) still exists for the report drill-downs that open a
 * record on top of themselves; the four list pages now route here instead.
 *
 * Layout follows the brief top to bottom: a header card with the quick-contact
 * actions, a row of summary tiles, the lifecycle strip, then two columns — the
 * communication log and the joined history on the left, the record's own facts
 * (contact, subscription, ownership, notes) on the right.
 */
import { useEffect, useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useApp } from '../store/AppContext.jsx';
import api from '../lib/api.js';
import { isOffline } from '../lib/storage.js';
import Icon from '../lib/icons.jsx';
import { digitsOnly } from '../lib/phone.js';
import {
  Avatar, PlanPill, StatusPill, PayPill, TypePill, OwnerPill, Pill, Dash, EmptyState
} from '../components/Primitives.jsx';
import { clientOfDeal, leadDetails } from '../domain/crm.js';
import {
  moduleList, isBundled, modulePrice, addonMonthly, amountOf, amountKnown, isBilled, mrrOf, cycleMonths
} from '../domain/pricing.js';
import { periodLabel } from '../lib/dates.js';

/** One label/value line inside a side panel. */
const Row = ({ label, children }) => (
  <div className="cdp-row">
    <span className="cdp-lab">{label}</span>
    <span className="cdp-val">{children ?? <Dash />}</span>
  </div>
);

const Panel = ({ icon, title, children, action }) => (
  <section className="cdp-panel">
    <h3><span className="cdp-ic"><Icon name={icon} /></span>{title}{action}</h3>
    <div className="cdp-panelbody">{children}</div>
  </section>
);

/* The channel a comms row draws itself as: its icon and the tone class the
   stylesheet colours. Kept here so a new channel is one line. */
const CHANNEL = {
  email: ['mail', 'Email'],
  whatsapp: ['chat', 'WhatsApp'],
  push: ['bell', 'Push'],
  call: ['phone', 'Call']
};

/* The lifecycle a won account passes through, in order. Each step lights up
   when its date is known and greys out until then (brief §12). */
const LIFECYCLE = [
  ['captured', 'Lead captured'],
  ['talk', 'Sales talk'],
  ['demo', 'Demo'],
  ['proposal', 'Proposal'],
  ['account', 'Account created'],
  ['payment', 'First payment'],
  ['renewal', 'Renewal']
];

export default function ClientDetail() {
  const { id } = useParams();
  const { rows, derive, S, crm, demos, fmtMoney, fmtDate, setSheet, patchClient } = useApp();
  const r = rows.find(x => String(x.id) === String(id));

  const [comms, setComms] = useState({ status: 'idle', data: [] });
  const [commFilter, setCommFilter] = useState('all');
  const [history, setHistory] = useState([]);
  const [note, setNote] = useState('');
  const [noteSaved, setNoteSaved] = useState(false);

  useEffect(() => { setNote(r ? (r.notes || '') : ''); }, [r?.id]);

  /* The record's communication log and joined history, fetched on open. Both
     fail quietly to an empty section rather than an error — an account that
     never had a mail sent to it is the common case, not a fault. Skipped
     offline, where there is no server to answer. */
  useEffect(() => {
    if (!r || isOffline()) { setComms({ status: 'ready', data: [] }); return; }
    let live = true;
    setComms({ status: 'loading', data: [] });
    api.clients.comms(r.id, { email: r.email, phone: r.phone })
      .then(res => { if (live) setComms({ status: 'ready', data: (res && res.data) || [] }); })
      .catch(() => { if (live) setComms({ status: 'ready', data: [] }); });
    api.clients.timeline(r.id)
      .then(res => { if (live) setHistory(Array.isArray(res) ? res : []); })
      .catch(() => { if (live) setHistory([]); });
    return () => { live = false; };
  }, [r?.id]);

  /* The deal this account came in on, matched the way the rest of the app
     matches the two: the link the conversion made, else the shared address.
     It carries the lead's capture facts and who captured it. */
  const fromDeal = useMemo(() => {
    if (!r) return null;
    const hit = (crm.deals || []).find(x => clientOfDeal(rows, x)?.id === r.id);
    if (hit) return hit;
    const email = String(r.email || '').trim().toLowerCase();
    if (!email) return null;
    return (crm.deals || []).find(x => {
      const f = leadDetails(crm, x);
      return String(f.email || '').trim().toLowerCase() === email;
    }) || null;
  }, [r, rows, crm]);

  const firstDemo = useMemo(() => {
    if (!r || !demos || !demos.bookings) return null;
    const email = String(r.email || '').trim().toLowerCase();
    const mine = demos.bookings
      .filter(b => (b.session && String(b.session.clientId || '') === String(r.id))
        || (email && String(b.invitee.email || '').toLowerCase() === email))
      .sort((a, b) => new Date(a.start) - new Date(b.start));
    return mine[0] || null;
  }, [r, demos]);

  if (!r) {
    return (
      <div className="cdp">
        <Link to="/clients" className="cdp-crumb"><Icon name="chev" />Clients</Link>
        <EmptyState icon="alert" tone="g-red" title="That client is no longer in the list">
          It may have been deleted, or filtered out.
        </EmptyState>
      </div>
    );
  }

  const d = derive(r);
  const mods = moduleList(r);
  const bundled = isBundled(S, r);
  const recurring = isBilled(S, r) || r.amount;
  const monthly = mrrOf(S, r, d);

  const location = [r.city, r.country].filter(Boolean).join(', ');
  const waNumber = digitsOnly(r.phone);

  /* Only tiles that actually hold a figure — a free account has no amount, no
     MRR and no due date, and a row of dashes says nothing the panels do not. */
  const tiles = [
    recurring && amountKnown(S, r) && [fmtMoney(amountOf(S, r)), `Amount${periodLabel(r.cycle) || ''}`],
    monthly > 0 && cycleMonths(r.cycle) > 1 && [fmtMoney(monthly), 'Monthly value'],
    recurring && d.next && [fmtDate(d.next),
      `Next payment${d.dueIn !== null ? ` · ${d.dueIn < 0 ? `${Math.abs(d.dueIn)}d late` : `in ${d.dueIn}d`}` : ''}`],
    d.act && [fmtDate(d.act), 'Last active'],
    d.con && [fmtDate(d.con), 'Last contacted']
  ].filter(Boolean);

  /* Each lifecycle step's date, from wherever the app already knows it. The
     middle three (sales talk, proposal) are only known when a worked deal sits
     behind the account; the rest come from the record and its payments. */
  const firstPayment = history
    .filter(e => e.kind === 'payment')
    .map(e => e.at)
    .sort((a, b) => new Date(a) - new Date(b))[0];
  const lifeDates = {
    captured: fromDeal ? (fromDeal.createdAt || fromDeal.created_at || null) : null,
    talk: null,
    demo: firstDemo ? firstDemo.start : null,
    proposal: null,
    account: d.reg || null,
    payment: firstPayment || (r.lastPaid || null),
    renewal: d.next || null
  };

  const filteredComms = commFilter === 'all'
    ? comms.data
    : comms.data.filter(c => c.channel === commFilter);

  const channelCounts = comms.data.reduce((m, c) => { m[c.channel] = (m[c.channel] || 0) + 1; return m; }, {});

  const saveNote = () => {
    patchClient(r.id, { notes: note });
    setNoteSaved(true);
    setTimeout(() => setNoteSaved(false), 1800);
  };

  return (
    <div className="cdp">
      <Link to="/clients" className="cdp-crumb"><Icon name="chev" />Clients</Link>

      {/* Header card: who this is, and the three ways to reach them. */}
      <header className="cdp-hero">
        <Avatar name={r.name} className="cdp-ava" />
        <div className="cdp-id">
          <div className="cdp-name">
            <h1>{r.name || 'Untitled'}</h1>
            <StatusPill value={r.status} />
            <PlanPill value={r.plan} />
          </div>
          <div className="cdp-sub">
            {location ? <span><Icon name="pin" />{location}</span> : null}
            <span className="cdp-code">ID {String(r.id).slice(0, 8)}</span>
          </div>
        </div>
        <div className="cdp-acts">
          <a className="cdp-btn" href={r.phone ? `tel:${r.phone}` : undefined} aria-disabled={!r.phone}>
            <Icon name="phone" />Call
          </a>
          <a className="cdp-btn" href={waNumber ? `https://wa.me/${waNumber}` : undefined}
            target="_blank" rel="noreferrer" aria-disabled={!waNumber}>
            <Icon name="chat" />WhatsApp
          </a>
          <a className="cdp-btn" href={r.email ? `mailto:${r.email}` : undefined} aria-disabled={!r.email}>
            <Icon name="mail" />Email
          </a>
          <button type="button" className="cdp-btn primary" onClick={() => setSheet({ kind: 'client', id: r.id })}>
            <Icon name="edit" />Edit client
          </button>
        </div>
      </header>

      {tiles.length ? (
        <div className="cdp-tiles">
          {tiles.map(([value, label]) => (
            <div className="cdp-tile" key={label}>
              <b>{value}</b>
              <small>{label}</small>
            </div>
          ))}
        </div>
      ) : null}

      {/* Lifecycle strip: the account's journey, each step lit when dated. */}
      <div className="cdp-life">
        {LIFECYCLE.map(([key, label], i) => {
          const at = lifeDates[key];
          return (
            <div key={key} className={`cdp-step${at ? ' on' : ''}`}>
              <span className="cdp-dot">{at ? <Icon name="check" /> : i + 1}</span>
              <b>{label}</b>
              <small>{at ? fmtDate(at) : '—'}</small>
            </div>
          );
        })}
      </div>

      <div className="cdp-cols">
        <div className="cdp-main">
          {/* Communication sent: every channel, with filter chips carrying
              their own counts (brief §12). */}
          <Panel icon="mail" title="Communication sent">
            <div className="cdp-chips">
              <button type="button" className={`cdp-chip${commFilter === 'all' ? ' on' : ''}`}
                onClick={() => setCommFilter('all')}>All <i>{comms.data.length}</i></button>
              {Object.entries(CHANNEL).map(([k, [, label]]) => channelCounts[k] ? (
                <button key={k} type="button" className={`cdp-chip${commFilter === k ? ' on' : ''}`}
                  onClick={() => setCommFilter(k)}>{label} <i>{channelCounts[k]}</i></button>
              ) : null)}
            </div>
            {comms.status === 'loading' ? (
              <div className="cdp-muted">Loading…</div>
            ) : filteredComms.length ? (
              <ul className="cdp-comms">
                {filteredComms.map((c, idx) => {
                  const [icon, label] = CHANNEL[c.channel] || ['flow', c.channel];
                  return (
                    <li key={c.ref || idx} className="cdp-comm">
                      <span className={`cdp-cchan ${c.channel}`}><Icon name={icon} /></span>
                      <div className="cdp-cwho">
                        <b>{c.subject || label}</b>
                        <small>{c.by || 'system'}{c.by ? '' : ''}</small>
                      </div>
                      <Pill className="cdp-cstat" tone="gr-slate">{c.status || 'Sent'}</Pill>
                      <span className="cdp-cwhen">{c.at ? fmtDate(c.at) : ''}</span>
                    </li>
                  );
                })}
              </ul>
            ) : (
              <div className="cdp-muted">
                {isOffline() ? 'Communication history is not available offline.' : 'Nothing sent to this client yet.'}
              </div>
            )}
          </Panel>

          {/* Full history: the record's own events, newest first. */}
          <Panel icon="flow" title="Full history">
            {history.length ? (
              <ul className="cdp-hist">
                {history.map((e, idx) => (
                  <li key={e.ref || idx} className={`cdp-hev ${e.kind}`}>
                    <span className="cdp-hdot" />
                    <div className="cdp-htext">
                      <b>{e.text}</b>
                      <small>{e.kind}{e.at ? ` · ${fmtDate(e.at)}` : ''}</small>
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="cdp-muted">No history recorded yet.</div>
            )}
          </Panel>
        </div>

        <aside className="cdp-side">
          <Panel icon="users" title="Contact">
            <Row label="Contact person">{r.contact || null}</Row>
            <Row label="Email">{r.email ? <a href={`mailto:${r.email}`}>{r.email}</a> : null}</Row>
            <Row label="Phone">{r.phone ? <a href={`tel:${r.phone}`}>{r.phone}</a> : null}</Row>
            <Row label="Source">{r.source || null}</Row>
          </Panel>

          <Panel icon="card" title="Subscription">
            <Row label="Plan"><PlanPill value={r.plan} /></Row>
            <Row label="Type"><TypePill value={d.type} /></Row>
            <Row label="Payment"><PayPill value={r.pay} paidValues={S.paidValues} /></Row>
            <Row label="Billing cycle">{r.cycle || null}</Row>
            <Row label="Trial ends">
              {d.noTrial ? <Pill tone="gr-slate">No trial</Pill> : d.trial ? fmtDate(d.trial) : null}
            </Row>
            <Row label="Promo">{r.promo || null}</Row>
            <Row label="Add-ons">
              {bundled ? <Pill tone="gr-luxe" icon="star">All modules</Pill>
                : mods.length ? mods.map(m => m.replace('XYRA ', '')).join(', ')
                  : null}
            </Row>
          </Panel>

          <Panel icon="users" title="Ownership">
            <Row label="Account owner"><OwnerPill name={r.handler} /></Row>
            <Row label="Lead captured by">{fromDeal ? (leadDetails(crm, fromDeal).capturedBy || fromDeal.owner || null) : null}</Row>
            <Row label="Lead record">
              {fromDeal
                ? <Link to="/pipeline" className="cdp-link" onClick={() => setSheet({ kind: 'dealView', id: fromDeal.id })}>Open the lead</Link>
                : null}
            </Row>
          </Panel>

          <Panel icon="file" title="Notes" action={
            <button type="button" className="cdp-save" onClick={saveNote}>
              {noteSaved ? 'Saved' : 'Save'}
            </button>
          }>
            <textarea className="cdp-notes" value={note} rows={5}
              placeholder="Add a note…" onChange={e => setNote(e.target.value)} />
          </Panel>
        </aside>
      </div>
    </div>
  );
}
